import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "../Icons/SearchIcon";
import ErrorMessage from "./ErrorMessage";

let debounceTimeout;

const DEFAULT_OPTIONS = [];

const SearchSelectInput = ({
  label = "Select Option",
  options = DEFAULT_OPTIONS,
  placeholder = "Search...",
  onSelect,
  className,
  errors,
  value = "",
  isLoading = false,
  onSearch = () => { },
  onAddClick = () => { },
  showAddButton = false,
  required = false,
  disabled = false,
  ...rest
}) => {
  const [search, setSearch] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    setSearch(value);
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const handleSelect = (option) => {
    setSearch(option?.label);
    setShowDropdown(false);
    onSelect(option);
  };

  const handleSearch = (data) => {
    setSearch(data);
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      onSearch(data);
    }, 600);
    setFilteredOptions(
      options.filter((opt) =>
        opt?.label?.toLowerCase()?.includes(data?.toLowerCase())
      )
    );
  };

  return (
    <div className={`mb-3 position-relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <div className="input-group">
        {/* Left search icon */}
        <span
          className="input-group-text bg-white border-end-0"
          id="search-icon"
        >
          <SearchIcon />
        </span>

        <input
          type="text"
          className={`form-control  ${showAddButton ? "pe-5" : ""}`}
          placeholder={placeholder}
          aria-label={label}
          aria-describedby="search-icon"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          autoComplete="off"
          disabled={disabled}
          {...rest}
        />

        {/* Optional right add button */}
        {showAddButton && (
          <button
            type="button"
            className="btn btn-outline-secondary position-absolute end-0 top-0 bottom-0"
            style={{ zIndex: 1050 }}
            onClick={onAddClick}
            title="Add Option"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-plus"
              viewBox="0 0 16 16"
            >
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
            </svg>
          </button>
        )}
      </div>

      {showDropdown && (
        <ul
          className="list-group position-absolute w-100 shadow-sm overflow-auto"
          style={{ maxHeight: "200px", zIndex: 1040 }}
        >
          {isLoading && (
            <li className="list-group-item text-muted">Loading...</li>
          )}
          {!isLoading && filteredOptions.length > 0
            ? filteredOptions.map((option, index) => (
              <li
                key={index}
                onClick={() => !option.disabled && handleSelect(option)}
                className={`list-group-item list-group-item-action ${option.label === search ? "active" : ""
                  } ${option.disabled ? "disabled bg-light text-muted" : ""}`}
                style={{ cursor: option.disabled ? "not-allowed" : "pointer" }}
              >
                <div>
                  <div style={{ fontSize: "0.85em" }}>{option.label}</div>
                  {option.subLabel && (
                    <small className="text-muted d-block" style={{ fontSize: "0.70em" }}>
                      {option.subLabel}
                    </small>
                  )}
                </div>
              </li>
            ))
            : !isLoading && (
              <li className="list-group-item text-muted">No options found</li>
            )}
        </ul>
      )}

      {<ErrorMessage message={errors} />}
    </div>
  );
};

export default SearchSelectInput;
